import { useState, useEffect, useRef } from 'react';
import { GlassCard } from '@/components/design/GlassCard';
import { NeonButton } from '@/components/design/NeonButton';
import { Plus, Brain, Clock, Trash2, Edit, Save, ArrowLeft } from 'lucide-react';
import { AdminAPI } from '@/services/api';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/utils/apiError';

/**
 * Variant sayı əvvəl koda 3 kimi bərkidilmişdi, müştərinin sualları isə
 * dörd variantlıdır (a/b/c/d). Boş qalan sondakı xanaları server kəsir,
 * yəni köhnə 3 variantlı quizlər olduğu kimi qalır.
 */
const OPTION_COUNT = 4;
const OPTION_LABELS = ['a', 'b', 'c', 'd'];

interface Question {
  id?: string;
  textEn: string;
  textAz: string;
  optionsEn: string[];
  optionsAz: string[];
  correct: number;
}

const emptyQuestion = (): Question => ({
  textEn: '',
  textAz: '',
  optionsEn: Array(OPTION_COUNT).fill(''),
  optionsAz: Array(OPTION_COUNT).fill(''),
  correct: 0,
});

/** İstənilən köhnə datanı tam OPTION_COUNT xanaya gətirir. */
const normalizeOptions = (options: unknown): string[] => {
  const source = Array.isArray(options) ? options : [];
  return Array.from({ length: OPTION_COUNT }, (_, i) => String(source[i] ?? ''));
};

interface Quiz {
  id: string;
  titleEn: string;
  titleAz: string;
  descriptionEn: string;
  descriptionAz: string;
  questions: any[]; 
}

export const QuizBuilderPage = () => {
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Editor State
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Localized Fields
  const [titleEn, setTitleEn] = useState('');
  const [titleAz, setTitleAz] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionAz, setDescriptionAz] = useState('');
  
  const [duration, setDuration] = useState(15);
  
  // Questions with localized content
  const [questions, setQuestions] = useState<Question[]>([emptyQuestion()]);

  // Language View Toggle
  const [lang, setLang] = useState<'en' | 'az'>('en');

  // Başlıq boş qalanda xananı işarələmək üçün
  const [titleError, setTitleError] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const { data } = await AdminAPI.gameplay.getQuizzes();
      // Map data to handle potential missing fields if old data exists
      const mapped = data.map((q: any) => ({
          ...q,
          titleEn: q.titleEn || q.title || '', // Fallback for transition
          titleAz: q.titleAz || '',
          descriptionEn: q.descriptionEn || q.description || '',
          descriptionAz: q.descriptionAz || ''
      }));
      setQuizzes(mapped);
    } catch (error) {
      toast.error('Failed to fetch quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setTitleEn('');
    setTitleAz('');
    setDescriptionEn('');
    setDescriptionAz('');
    setQuestions([emptyQuestion()]);
    setTitleError(false);
    setView('editor');
  };

  const handleEdit = (quiz: any) => {
    setEditingId(quiz.id);
    setTitleEn(quiz.titleEn || quiz.title || '');
    setTitleAz(quiz.titleAz || '');
    setDescriptionEn(quiz.descriptionEn || quiz.description || '');
    setDescriptionAz(quiz.descriptionAz || '');

    // Məzmunu olan dildə açırıq: yalnız azərbaycanca doldurulmuş quiz
    // ingilis görünüşündə boş görünürdü və «data itdi» təəssüratı yaradırdı
    if (quiz.titleEn || quiz.description || quiz.descriptionEn) setLang('en');
    else if (quiz.titleAz || quiz.descriptionAz) setLang('az');


    /**
     * Variantlar bazada JSON mətn kimi saxlanılır. Köhnə datada 3, bəzən 0 variant
     * ola bilər — hamısını dörd xanaya açırıq, yazılmayanlar boş qalır.
     * Əvvəlki kod uzunluq düz 3 deyilsə bütün variantları ATIRDI.
     */
    const parseOptions = (raw: any): string[] => {
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string' && raw.trim()) {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const parsedQuestions: Question[] = (quiz.questions || []).map((q: any) => ({
        id: q.id,
        textEn: q.textEn || q.text || '',
        textAz: q.textAz || '',
        optionsEn: normalizeOptions(parseOptions(q.optionsEn ?? q.options)),
        optionsAz: normalizeOptions(parseOptions(q.optionsAz)),
        correct: typeof q.correctOption === 'number' ? q.correctOption : 0,
    }));

    setQuestions(parsedQuestions.length > 0 ? parsedQuestions : [emptyQuestion()]);
    setView('editor');
  };

  const handleDelete = async (id: string) => {
    if(!confirm('Are you sure?')) return;
    try {
        await AdminAPI.gameplay.deleteQuiz(id);
        toast.success('Quiz deleted');
        fetchQuizzes();
    } catch (e) {
        toast.error(apiErrorMessage(e, 'Failed to delete quiz'));
    }
  }

  const handleSave = async () => {
    /**
     * Başlıq xanası sol paneldədir, sual xanaları isə böyük və gözə çarpandır —
     * istifadəçilər sualı doldurub başlığı gözdən qaçırır və ingiliscə,
     * hansı sahəni göstərmədən xəbərdarlıq alırdılar. İndi xana işarələnir,
     * fokus ora keçir və mesaj azərbaycancadır.
     */
    if (!titleEn.trim() && !titleAz.trim()) {
      setTitleError(true);
      titleInputRef.current?.focus();
      titleInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return toast.error('Quizin başlığını yazın (ən azı bir dildə)');
    }
    setTitleError(false);

    /**
     * Server variantları massiv kimi gözləyir və özü JSON-a çevirir.
     * Əvvəl panel hazır Prisma əmri (`questions: { create: [...] }`) göndərirdi —
     * yəni brauzer birbaşa baza əməliyyatı diktə edirdi.
     */
    const payload = {
        titleEn,
        titleAz,
        descriptionEn,
        descriptionAz,
        questions: questions.map(q => ({
            textEn: q.textEn,
            textAz: q.textAz,
            optionsEn: q.optionsEn,
            optionsAz: q.optionsAz,
            correctOption: q.correct,
        })),
    };

    try {
        setLoading(true);
        if (editingId) {
             // Suallar da daxil olmaqla tam yenilənmə
             await AdminAPI.gameplay.updateQuiz(editingId, payload);
             toast.success('Quiz yeniləndi');
        } else {
             await AdminAPI.gameplay.createQuiz(payload);
             toast.success('Quiz yaradıldı');
        }
        setView('list');
        fetchQuizzes();
    } catch (error) {
        console.error(error);
        toast.error(apiErrorMessage(error, 'Failed to save quiz'));
    } finally {
        setLoading(false);
    }
  };

  const addQuestion = () => {
    setQuestions([...questions, emptyQuestion()]);
  };

  const removeQuestion = (idx: number) => {
      // Ən azı bir sual qalmalıdır — server boş quizi qəbul etmir
      if (questions.length === 1) {
          toast.error('Quizdə ən azı bir sual olmalıdır');
          return;
      }
      setQuestions(questions.filter((_, i) => i !== idx));
  }

  const updateQuestion = (idx: number, field: keyof Question, value: any) => {
    const newQuestions = [...questions];
    newQuestions[idx] = { ...newQuestions[idx], [field]: value };
    setQuestions(newQuestions);
  };

  const updateOption = (qIdx: number, oIdx: number, val: string, lang: 'en' | 'az') => {
      const newQuestions = [...questions];
      const targetOptions = lang === 'en' ? newQuestions[qIdx].optionsEn : newQuestions[qIdx].optionsAz;
      const newOptions = [...targetOptions];
      
      newOptions[oIdx] = val;
      
      if (lang === 'en') newQuestions[qIdx].optionsEn = newOptions;
      else newQuestions[qIdx].optionsAz = newOptions;

      setQuestions(newQuestions);
  }

  if (view === 'list') {
      return (
          <div className="space-y-6">
              <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Quizzes</h2>
                    <p className="text-muted-foreground">Manage assessment quizzes.</p>
                  </div>
                  <NeonButton onClick={handleCreate}>
                      <Plus className="w-4 h-4 mr-2" /> Create Quiz
                  </NeonButton>
              </div>

              <div className="grid gap-4">
                  {quizzes.map(quiz => (
                      <GlassCard key={quiz.id} className="flex items-center justify-between p-6">
                          <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                                  <Brain className="w-6 h-6" />
                              </div>
                              <div>
                                  <h3 className="font-bold text-lg">{quiz.titleEn || quiz.titleAz}</h3>
                                  <p className="text-sm text-muted-foreground">{quiz.descriptionEn || quiz.descriptionAz}</p>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                                      <span>{quiz.questions?.length || 0} Questions</span>
                                      {quiz.titleAz && <span className="bg-primary/20 text-primary px-2 rounded">AZ</span>}
                                      {quiz.titleEn && <span className="bg-primary/20 text-primary px-2 rounded">EN</span>}
                                  </div>
                              </div>
                          </div>
                          <div className="flex items-center gap-2">
                              <NeonButton variant="ghost" size="sm" onClick={() => handleEdit(quiz)}>
                                  <Edit className="w-4 h-4" />
                              </NeonButton>
                              <NeonButton variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(quiz.id)}>
                                  <Trash2 className="w-4 h-4" />
                              </NeonButton>
                          </div>
                      </GlassCard>
                  ))}
                  {quizzes.length === 0 && !loading && (
                      <div className="text-center py-12 text-muted-foreground">No quizzes found. Create one!</div>
                  )}
              </div>
          </div>
      )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
         <div className="flex items-center gap-4">
            <NeonButton variant="ghost" size="sm" className="rounded-full w-10 h-10 p-0" onClick={() => setView('list')}>
                <ArrowLeft className="w-5 h-5" />
            </NeonButton>
            <div>
                <h2 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">
                    {editingId ? 'Edit Quiz' : 'New Quiz'}
                </h2>
            </div>
        </div>
        
        {/* Language Toggle */}
        <div className="flex items-center bg-background/50 rounded-lg p-1 border border-white/10">
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${lang === 'en' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-white'}`}
            >
              English
            </button>
            <button
              onClick={() => setLang('az')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${lang === 'az' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-white'}`}
            >
              Azerbaijani
            </button>
        </div>

        <div className="flex gap-2">
           <NeonButton variant="secondary" onClick={() => setView('list')}>Cancel</NeonButton>
           {/* Saxlama hər iki dili birlikdə yazır — düymə dil adı daşımamalıdır */}
           <NeonButton onClick={handleSave} disabled={loading}>
               <Save className="w-4 h-4 mr-2" /> {editingId ? 'Yenilə' : 'Saxla'}
           </NeonButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quiz Meta */}
        <div className="space-y-6">
          <GlassCard>
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Quiz Details ({lang.toUpperCase()})</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                    ref={titleInputRef}
                    className={`w-full bg-background/50 border rounded-lg px-3 py-2 text-sm text-foreground transition-colors ${
                      titleError ? 'border-red-500 ring-1 ring-red-500/40' : 'border-white/10'
                    }`}
                    placeholder={lang === 'en' ? "e.g. Political Compass" : "Məs: Siyasi Kompas"}
                    value={lang === 'en' ? titleEn : titleAz}
                    onChange={e => {
                      setTitleError(false);
                      lang === 'en' ? setTitleEn(e.target.value) : setTitleAz(e.target.value);
                    }}
                />
                {titleError && (
                  <p className="text-xs text-red-400">
                    Başlıq ən azı bir dildə doldurulmalıdır — hazırda «{lang === 'en' ? 'English' : 'Azerbaijani'}» görünüşündəsiniz.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase">Description</label>
                <textarea 
                    className="w-full bg-background/50 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none h-24 text-foreground" 
                    placeholder={lang === 'en' ? "Brief description..." : "Qısa təsvir..."}
                    value={lang === 'en' ? descriptionEn : descriptionAz}
                    onChange={e => lang === 'en' ? setDescriptionEn(e.target.value) : setDescriptionAz(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                 <label className="text-xs font-medium text-muted-foreground uppercase">Duration (mins)</label>
                 <div className="relative">
                   <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                   <input 
                        className="w-full bg-background/50 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground" 
                        type="number" 
                        value={duration}
                        onChange={e => setDuration(Number(e.target.value))}
                    />
                 </div>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Question Builder */}
        <div className="lg:col-span-2 space-y-4">
          {questions.map((q, idx) => (
            <GlassCard key={idx} className="relative group">
              <div className="absolute right-4 top-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                    className="p-2 hover:bg-red-500/10 text-muted-foreground hover:text-red-400 rounded-lg transition-colors"
                    onClick={() => removeQuestion(idx)}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex items-start gap-4">
                 <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 text-muted-foreground font-mono text-sm">
                   {idx + 1}
                 </div>
                 <div className="flex-1 space-y-4">
                   <input 
                     className="w-full bg-transparent border-b border-white/10 px-0 py-2 text-lg font-medium focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50 text-foreground"
                     placeholder={lang === 'en' ? "Enter question text..." : "Sual mətnini daxil edin..."}
                     value={lang === 'en' ? q.textEn : q.textAz}
                     onChange={e => updateQuestion(idx, lang === 'en' ? 'textEn' : 'textAz', e.target.value)}
                   />
                   
                   <div className="space-y-2 pl-4 border-l-2 border-white/5">
                     {(lang === 'en' ? q.optionsEn : q.optionsAz).map((opt, optIdx) => (
                       <div key={optIdx} className="flex items-center gap-2">
                         <div 
                            className={`w-4 h-4 rounded-full border flex-shrink-0 cursor-pointer ${
                                q.correct === optIdx ? 'border-primary bg-primary' : 'border-white/20'
                            }`} 
                            onClick={() => updateQuestion(idx, 'correct', optIdx)}
                         />
                         <span className="w-4 text-xs font-mono text-muted-foreground/60 flex-shrink-0">
                           {OPTION_LABELS[optIdx]})
                         </span>
                         <input
                           className="flex-1 bg-transparent px-2 py-1 text-sm focus:outline-none focus:bg-white/5 rounded transition-colors text-foreground"
                           placeholder={
                             optIdx < 2
                               ? `Variant ${OPTION_LABELS[optIdx]}`
                               : `Variant ${OPTION_LABELS[optIdx]} (istəyə bağlı)`
                           }
                           value={opt}
                           onChange={e => updateOption(idx, optIdx, e.target.value, lang)}
                         />
                       </div>
                     ))}
                   </div>
                 </div>
              </div>
            </GlassCard>
          ))}

          <NeonButton variant="secondary" className="w-full border-dashed" onClick={addQuestion}>
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </NeonButton>
        </div>
      </div>
    </div>
  );
};
